/**
 * +atmos: time of day (presentation only; the sim never sees it). A look is every colour the
 * outdoor scene takes from the sky: sun direction and key light, fill, fog, sky gradient, clouds,
 * the distant mountains and the grade. `day` is the theme's own look; dawn, golden, dusk and a
 * short readable blue night are painted presets. The cycle runs on the wall clock (players in the
 * same zone share it): a 40-minute day that is mostly daytime, short golden and dusk phases and a
 * brief blue night (never full darkness).
 *
 * Pure (no three.js scene access): blending and the cycle are unit-tested.
 */

export type TodPreset = 'dawn' | 'day' | 'golden' | 'dusk' | 'night';
export const TOD_PRESETS: readonly TodPreset[] = ['dawn', 'day', 'golden', 'dusk', 'night'];
export type TodMode = 'cycle' | 'day';

type V3 = [number, number, number];

/** Every value a look sets. Colours are linear RGB; `sun` is a three.js world direction (toward the sun). */
export interface AtmosLook {
  sun: V3;
  key: V3;
  keyIntensity: number;
  hemiSky: V3;
  hemiGround: V3;
  hemiIntensity: number;
  fog: V3;
  fogTint: V3;
  skyTop: V3;
  skyMid: V3;
  skyHorizon: V3;
  cloud: V3;
  cloudShade: V3;
  /** Distant mountains: body multiplier. */
  land: V3;
  sat: number;
  gain: V3;
  lift: V3;
  /** Cloud-shadow strength on the ground. */
  cloudShadow: number;
  /** Light-shaft strength (0..1+). */
  shafts: number;
  /** 0 = day … 1 = night (stars, moon disc instead of the sun). */
  night: number;
  /** Aerial-perspective amount (× the theme's max haze). */
  haze: number;
}

/** sRGB hex → linear RGB (matches THREE.Color.setHex under colour management). */
export function hexLin(hex: number): V3 {
  const f = (c: number): number => {
    const v = c / 255;
    return v < 0.04045 ? v * 0.0773993808 : Math.pow(v * 0.9478672986 + 0.0521327014, 2.4);
  };
  return [f((hex >> 16) & 255), f((hex >> 8) & 255), f(hex & 255)];
}

/** Default sun (the original key direction). */
export const DAY_SUN: V3 = [-0.55, 1.0, 0.62];

/** Theme inputs for the `day` look. */
export interface DayInputs {
  sky: { top: number; mid: number; horizon: number; cloud: number; cloudShade: number };
  lighting: {
    fog: V3 | readonly number[];
    hemiSky: number;
    hemiGround: number;
    hemiIntensity: number;
    key: number;
    keyIntensity?: number;
    fogTint?: readonly number[];
    fogShape?: readonly number[];
    sat?: number;
    gain?: readonly number[];
    lift: readonly number[];
  };
}

const v3 = (a: readonly number[] | undefined, d: V3): V3 => (a && a.length >= 3 ? [a[0]!, a[1]!, a[2]!] : d);

export function dayLook(t: DayInputs): AtmosLook {
  const L = t.lighting;
  return {
    sun: DAY_SUN,
    key: hexLin(L.key),
    keyIntensity: L.keyIntensity ?? 3.2,
    hemiSky: hexLin(L.hemiSky),
    hemiGround: hexLin(L.hemiGround),
    hemiIntensity: L.hemiIntensity,
    fog: v3(L.fog as readonly number[], [0.5, 0.67, 0.9]),
    fogTint: v3(L.fogTint, [1, 1, 1]),
    skyTop: hexLin(t.sky.top),
    skyMid: hexLin(t.sky.mid),
    skyHorizon: hexLin(t.sky.horizon),
    cloud: hexLin(t.sky.cloud),
    cloudShade: hexLin(t.sky.cloudShade),
    land: [1, 1, 1],
    sat: L.sat ?? 1.12,
    gain: v3(L.gain, [1.03, 1.0, 0.97]),
    lift: v3(L.lift, [0, 0, 0]),
    cloudShadow: 0.4,
    shafts: 0.55,
    night: 0,
    haze: 1,
  };
}

/** The painted presets (absolute; `day` comes from the theme). `golden` = the trailer's golden look. */
export function presetLook(p: Exclude<TodPreset, 'day'>): AtmosLook {
  switch (p) {
    case 'golden':
      // Low warm late-afternoon sun from the west, long shadows, peach horizon, less haze (debug/capture.ts 'golden').
      return {
        sun: [-0.9, 0.42, 0.18],
        key: hexLin(0xffc58a),
        keyIntensity: 3.6,
        hemiSky: hexLin(0xf2d9bd),
        hemiGround: hexLin(0x4b3b3a),
        hemiIntensity: 0.62,
        fog: [0.93, 0.8, 0.66],
        fogTint: [1.02, 0.93, 0.86],
        skyTop: hexLin(0x3f6db8),
        skyMid: hexLin(0x8fb0d6),
        skyHorizon: hexLin(0xffc796),
        cloud: hexLin(0xfff1de),
        cloudShade: hexLin(0xb9909a),
        land: [1.06, 0.9, 0.82],
        sat: 1.24,
        gain: [1.08, 1.0, 0.9],
        lift: [0.006, 0.004, 0.012],
        cloudShadow: 0.32,
        shafts: 1,
        night: 0,
        haze: 0.72,
      };
    case 'dawn':
      return {
        sun: [0.86, 0.34, 0.36],
        key: hexLin(0xffd6b8),
        keyIntensity: 3.0,
        hemiSky: hexLin(0xdcd6f2),
        hemiGround: hexLin(0x4c4a5c),
        hemiIntensity: 0.7,
        fog: [0.78, 0.72, 0.84],
        fogTint: [0.96, 0.9, 1.02],
        skyTop: hexLin(0x3a64b4),
        skyMid: hexLin(0x9cb2dc),
        skyHorizon: hexLin(0xffd8c6),
        cloud: hexLin(0xfff0ec),
        cloudShade: hexLin(0xa49ac0),
        land: [0.98, 0.9, 0.96],
        sat: 1.16,
        gain: [1.05, 1.0, 1.0],
        lift: [0.008, 0.006, 0.016],
        cloudShadow: 0.3,
        shafts: 0.9,
        night: 0,
        haze: 0.8,
      };
    case 'dusk':
      return {
        sun: [-0.96, 0.2, 0.08],
        key: hexLin(0xff9e70),
        keyIntensity: 2.6,
        hemiSky: hexLin(0xa498cc),
        hemiGround: hexLin(0x3c3650),
        hemiIntensity: 0.8,
        fog: [0.5, 0.42, 0.6],
        fogTint: [0.92, 0.82, 0.98],
        skyTop: hexLin(0x26336e),
        skyMid: hexLin(0x6e6cae),
        skyHorizon: hexLin(0xffa27e),
        cloud: hexLin(0xffc6a6),
        cloudShade: hexLin(0x6c5c8e),
        land: [0.74, 0.64, 0.8],
        sat: 1.18,
        gain: [1.06, 0.97, 0.96],
        lift: [0.012, 0.008, 0.022],
        cloudShadow: 0.15,
        shafts: 0.6,
        night: 0.15,
        haze: 0.8,
      };
    case 'night':
      // A short, readable blue night: moonlit key, strong cool fill, never black.
      return {
        sun: [0.35, 0.85, -0.4],
        key: hexLin(0xa9c2ff),
        keyIntensity: 1.9,
        hemiSky: hexLin(0x7088cc),
        hemiGround: hexLin(0x2c344c),
        hemiIntensity: 0.95,
        fog: [0.11, 0.16, 0.3],
        fogTint: [0.8, 0.9, 1.15],
        skyTop: hexLin(0x07122c),
        skyMid: hexLin(0x152858),
        skyHorizon: hexLin(0x3a5590),
        cloud: hexLin(0x6676a8),
        cloudShade: hexLin(0x26325a),
        land: [0.36, 0.44, 0.66],
        sat: 1.02,
        gain: [0.94, 1.0, 1.12],
        lift: [0.012, 0.018, 0.036],
        cloudShadow: 0.12,
        shafts: 0,
        night: 1,
        haze: 0.75,
      };
  }
}

/** Component-wise blend (the sun direction is renormalised). */
export function mixLook(a: AtmosLook, b: AtmosLook, t: number, out?: AtmosLook): AtmosLook {
  const k = Math.min(1, Math.max(0, t));
  const m = (x: number, y: number): number => x + (y - x) * k;
  const m3 = (x: V3, y: V3): V3 => [m(x[0], y[0]), m(x[1], y[1]), m(x[2], y[2])];
  const sun = m3(a.sun, b.sun);
  const l = Math.hypot(sun[0], sun[1], sun[2]) || 1;
  const r: AtmosLook = {
    sun: [sun[0] / l, sun[1] / l, sun[2] / l],
    key: m3(a.key, b.key),
    keyIntensity: m(a.keyIntensity, b.keyIntensity),
    hemiSky: m3(a.hemiSky, b.hemiSky),
    hemiGround: m3(a.hemiGround, b.hemiGround),
    hemiIntensity: m(a.hemiIntensity, b.hemiIntensity),
    fog: m3(a.fog, b.fog),
    fogTint: m3(a.fogTint, b.fogTint),
    skyTop: m3(a.skyTop, b.skyTop),
    skyMid: m3(a.skyMid, b.skyMid),
    skyHorizon: m3(a.skyHorizon, b.skyHorizon),
    cloud: m3(a.cloud, b.cloud),
    cloudShade: m3(a.cloudShade, b.cloudShade),
    land: m3(a.land, b.land),
    sat: m(a.sat, b.sat),
    gain: m3(a.gain, b.gain),
    lift: m3(a.lift, b.lift),
    cloudShadow: m(a.cloudShadow, b.cloudShadow),
    shafts: m(a.shafts, b.shafts),
    night: m(a.night, b.night),
    haze: m(a.haze, b.haze),
  };
  return out ? Object.assign(out, r) : r;
}

/** Length of one full day (ms). */
export const DAY_MS = 40 * 60 * 1000;

/**
 * The cycle's keyframes (phase 0..1 → preset), wrapping at 1: dawn, ~24 min of day, a short golden
 * hour, dusk, ~3 min of blue night, back to dawn.
 */
export const CYCLE: readonly { at: number; p: TodPreset }[] = [
  { at: 0.0, p: 'dawn' },
  { at: 0.07, p: 'day' },
  { at: 0.66, p: 'day' },
  { at: 0.75, p: 'golden' },
  { at: 0.83, p: 'dusk' },
  { at: 0.9, p: 'night' },
  { at: 0.97, p: 'night' },
];

/** Phase (0..1) of the day at a wall-clock time (ms). */
export function cyclePhase(nowMs: number, dayMs = DAY_MS): number {
  const p = (nowMs % dayMs) / dayMs;
  return p < 0 ? p + 1 : p;
}

/** The two presets around `phase` and the (eased) blend between them. */
export function cycleAt(phase: number): { a: TodPreset; b: TodPreset; t: number } {
  const ph = ((phase % 1) + 1) % 1;
  for (let i = 0; i < CYCLE.length; i++) {
    const k0 = CYCLE[i]!;
    const k1 = CYCLE[(i + 1) % CYCLE.length]!;
    const end = i + 1 < CYCLE.length ? k1.at : 1 + CYCLE[0]!.at;
    if (ph >= k0.at && ph < end) {
      const u = (ph - k0.at) / (end - k0.at);
      return { a: k0.p, b: k1.p, t: u * u * (3 - 2 * u) };
    }
  }
  return { a: 'day', b: 'day', t: 0 };
}

/** Night for the ambience beds (the night forest) once the blend is mostly night. */
export function isNight(look: Pick<AtmosLook, 'night'>): boolean {
  return look.night > 0.55;
}
